import type { GardenProject } from "@mity-garden/domain";
import type { ProjectRepository } from "@mity-garden/persistence";

// ─── Browser file download helper ─────────────────────────────────────────────

function triggerDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Sanitised filename ────────────────────────────────────────────────────────

function safeFilename(name: string): string {
  return name.replace(/[^a-z0-9_\-. ]/gi, "_").trim() || "garden";
}

// ─── JSON export ───────────────────────────────────────────────────────────────

export async function exportProjectAsJSON(
  project: GardenProject,
  repo: ProjectRepository
): Promise<void> {
  const json = await repo.exportJSON(project);
  const filename = `${safeFilename(project.name)}.garden.json`;
  triggerDownload(json, filename, "application/json");
}

// ─── Text proposal export ─────────────────────────────────────────────────────

export function exportProjectAsText(project: GardenProject): void {
  const lines: string[] = [
    "═══════════════════════════════════════════════════════",
    `  GARDEN DESIGN PROPOSAL`,
    `  ${project.name}`,
    "═══════════════════════════════════════════════════════",
    "",
    `Created:  ${new Date(project.createdAt).toLocaleDateString()}`,
    `Updated:  ${new Date(project.updatedAt).toLocaleDateString()}`,
    "",
    "── DIMENSIONS ─────────────────────────────────────────",
    `  Width :  ${String(project.dimensions.width)} ${project.unit === "metric" ? "m" : "ft"}`,
    `  Height:  ${String(project.dimensions.height)} ${project.unit === "metric" ? "m" : "ft"}`,
    `  Area  :  ${(project.dimensions.width * project.dimensions.height).toFixed(1)} ${project.unit === "metric" ? "m²" : "ft²"}`,
    "",
  ];

  lines.push("── STYLE ──────────────────────────────────────────────");
  lines.push(`  ${project.style.charAt(0).toUpperCase() + project.style.slice(1)}`);
  lines.push("");

  if (project.goals.length > 0) {
    lines.push("── GOALS ──────────────────────────────────────────────");
    for (const g of project.goals) {
      lines.push(`  • ${g.replace(/-/g, " ")}`);
    }
    lines.push("");
  }

  const allElements = project.layers.flatMap((l) => l.elements);
  if (allElements.length > 0) {
    lines.push("── ELEMENTS ───────────────────────────────────────────");
    for (const el of allElements) {
      const w = el.size.width.toFixed(1);
      const h = el.size.height.toFixed(1);
      const x = el.position.x.toFixed(1);
      const y = el.position.y.toFixed(1);
      const unit = project.unit === "metric" ? "m" : "ft";
      lines.push(`  • ${el.assetId.replace(/-/g, " ")} (${w}×${h}${unit} @ ${x},${y})`);
    }
    lines.push("");
  }

  lines.push("── LAYERS ─────────────────────────────────────────────");
  for (const layer of project.layers) {
    const vis = layer.visible ? "visible" : "hidden";
    lines.push(`  • ${layer.name} [${vis}, ${String(layer.elements.length)} element(s)]`);
  }
  lines.push("");
  lines.push("═══════════════════════════════════════════════════════");
  lines.push("  Generated by mITyGarden");
  lines.push("═══════════════════════════════════════════════════════");

  const text = lines.join("\n");
  const filename = `${safeFilename(project.name)}-proposal.txt`;
  triggerDownload(text, filename, "text/plain");
}
